// DEPRECATED: consolidated into invariantAggregators.js — kept for backward compat
// ─────────────────────────────────────────────────────────────────────────────
// ThemeAggregator.js
// Core message themes: top-level theme ranking, per-theme argument buckets,
// and the themes_convincing question.
// Palette mirrors the client deck.
// ─────────────────────────────────────────────────────────────────────────────

// Theme brand colors — used consistently across all three aggregator files
export const THEME_COLORS = {
  choice:      "#1A7A78", // teal        — Freedom to Choose
  everyday:    "#2D6A9F", // mid-blue    — Everyday Essentials
  safety:      "#1E3A5F", // dark navy   — Product Safety
  economy:     "#E8722A", // orange      — Economy
  environment: "#8C9BA5", // slate-gray  — Environment
};

// Shared DISTINCT ramp (deck-harmonised)
export const DISTINCT = [
  "#1A7A78", // teal
  "#2D6A9F", // mid-blue
  "#1E3A5F", // dark navy
  "#E8722A", // orange
  "#8C9BA5", // slate-gray
  "#4AA8A6", // light teal
  "#5B8DB8", // light blue
  "#C45A1E", // burnt orange
  "#3A5F82", // steel blue
  "#B0BEC5", // light gray
];

export function getThemeColours() {
  return DISTINCT;
}

export function getThemeConfig() {
  const groupsById = {

    themes_convincing: {
      type: "categorical",
      label: "Most convincing theme",
      fulltext: "Which do you find most convincing for you personally?",
      keys: [
        "theme_convincing__choice_pred_n",
        "theme_convincing__everyday_pred_n",
        "theme_convincing__safety_pred_n",
        "theme_convincing__economy_pred_n",
        "theme_convincing__environ_pred_n",
      ],
      labels: {
        "theme_convincing__choice_pred_n":   "Freedom to Choose",
        "theme_convincing__everyday_pred_n": "Everyday Essentials",
        "theme_convincing__safety_pred_n":   "Product Safety",
        "theme_convincing__economy_pred_n":  "Economy",
        "theme_convincing__environ_pred_n":  "Environment",
      },
      colors: {
        "theme_convincing__choice_pred_n":   THEME_COLORS.choice,
        "theme_convincing__everyday_pred_n": THEME_COLORS.everyday,
        "theme_convincing__safety_pred_n":   THEME_COLORS.safety,
        "theme_convincing__economy_pred_n":  THEME_COLORS.economy,
        "theme_convincing__environ_pred_n":  THEME_COLORS.environment,
      },
      schema: "single",
    },

    choice_arguments: {
      type: "categorical",
      label: "Most convincing choice argument",
      fulltext: "Most convincing argument from the choice theme",
      keys: [
        "choice_args__red_tape_pred_n",
        "choice_args__consumer_value_pred_n",
        "choice_args__parents_decide_pred_n",
        "choice_args__people_dont_want_pred_n",
      ],
      labels: {
        "choice_args__red_tape_pred_n":          "Red tape / government overreach",
        "choice_args__consumer_value_pred_n":    "Consumer value & affordability",
        "choice_args__parents_decide_pred_n":    "Parents should decide for children",
        "choice_args__people_dont_want_pred_n":  "People don't want government in personal decisions",
      },
      colors: {
        "choice_args__red_tape_pred_n":          "#1A7A78", // teal (theme anchor)
        "choice_args__consumer_value_pred_n":    "#2D6A9F", // blue
        "choice_args__parents_decide_pred_n":    "#E8722A", // orange
        "choice_args__people_dont_want_pred_n":  "#6B5CA5", // purple
      },
      schema: "single",
    },

    economic_arguments: {
      type: "categorical",
      label: "Most convincing economic argument",
      fulltext: "Most convincing argument from the economic theme",
      keys: [
        "econ_args__raise_costs_pred_n",
        "econ_args__small_biz_pred_n",
        "econ_args__ladder_pred_n",
        "econ_args__millions_jobs_pred_n",
        "econ_args__competitiveness_pred_n",
      ],
      labels: {
        "econ_args__raise_costs_pred_n":      "Unscientific policies raise costs & cut jobs",
        "econ_args__small_biz_pred_n":        "Small businesses depend on product access",
        "econ_args__ladder_pred_n":           "Industry is a ladder of opportunity",
        "econ_args__millions_jobs_pred_n":    "Millions of jobs & billions to the economy",
        "econ_args__competitiveness_pred_n":  "Boosts U.S. global competitiveness",
      },
      colors: {
        "econ_args__raise_costs_pred_n":      "#E8722A", // orange (theme anchor)
        "econ_args__small_biz_pred_n":        "#C45A1E", // burnt orange
        "econ_args__ladder_pred_n":           "#2D6A9F", // blue
        "econ_args__millions_jobs_pred_n":    "#1A7A78", // teal
        "econ_args__competitiveness_pred_n":  "#6B5CA5", // purple
      },
      schema: "single",
    },

    everyday_arguments: {
      type: "categorical",
      label: "Most convincing everyday argument",
      fulltext: "Most convincing argument from the everyday theme",
      keys: [
        "everyday_args__routines_pred_n",
        "everyday_args__necessities_pred_n",
        "everyday_args__public_health_pred_n",
        "everyday_args__disrupted_pred_n",
      ],
      labels: {
        "everyday_args__routines_pred_n":      "6–12 products used daily",
        "everyday_args__necessities_pred_n":   "Basic hygiene necessities",
        "everyday_args__public_health_pred_n": "Essential during COVID-19 / public health",
        "everyday_args__disrupted_pred_n":     "Restrictions disrupt everyday routines",
      },
      colors: {
        "everyday_args__routines_pred_n":      "#2D6A9F", // mid-blue (theme anchor)
        "everyday_args__necessities_pred_n":   "#1A7A78", // teal
        "everyday_args__public_health_pred_n": "#E8722A", // orange
        "everyday_args__disrupted_pred_n":     "#6B5CA5", // purple
      },
      schema: "single",
    },

    safety_arguments: {
      type: "categorical",
      label: "Most convincing safety argument",
      fulltext: "Most convincing argument from the safety theme",
      keys: [
        "safety_args__regulated_pred_n",
        "safety_args__science_pred_n",
        "safety_args__all_consumers_pred_n",
        "safety_args__harmonized_pred_n",
        "safety_args__continuous_review_pred_n",
        "safety_args__counterfeit_pred_n",
      ],
      labels: {
        "safety_args__regulated_pred_n":         "Products are federally regulated",
        "safety_args__science_pred_n":           "Decisions should be science-based",
        "safety_args__all_consumers_pred_n":     "Standards apply to all consumers",
        "safety_args__harmonized_pred_n":        "Supports harmonized FDA oversight",
        "safety_args__continuous_review_pred_n": "Industry continuously reviews ingredients",
        "safety_args__counterfeit_pred_n":       "Restrictions push consumers to counterfeits",
      },
      colors: {
        "safety_args__regulated_pred_n":         "#1E3A5F", // dark navy (theme anchor)
        "safety_args__science_pred_n":           "#2D6A9F", // blue
        "safety_args__all_consumers_pred_n":     "#1A7A78", // teal
        "safety_args__harmonized_pred_n":        "#E8722A", // orange
        "safety_args__continuous_review_pred_n": "#6B5CA5", // purple
        "safety_args__counterfeit_pred_n":       "#8C9BA5", // slate-gray
      },
      schema: "single",
    },

    environment_arguments: {
      type: "categorical",
      label: "Most convincing environment argument",
      fulltext: "Most convincing argument from the environment theme",
      keys: [
        "envir_args__science_pred_n",
        "envir_args__investing_pred_n",
        "envir_args__advancing_pred_n",
      ],
      labels: {
        "envir_args__science_pred_n":    "Guided by science & life-cycle analysis",
        "envir_args__investing_pred_n":  "Investing in environmental innovation",
        "envir_args__advancing_pred_n":  "Advancing responsible solutions",
      },
      colors: {
        "envir_args__science_pred_n":    "#8C9BA5", // slate-gray (theme anchor)
        "envir_args__investing_pred_n":  "#4B7A5D", // green
        "envir_args__advancing_pred_n":  "#2D6A9F", // blue
      },
      schema: "single",
    },

    // industry_posneg_change: {
    //   type: "categorical",
    //   label: "Industry favorability change after message exposure",
    //   fulltext: "Change in industry favorability (pre vs. post message exposure)",
    //   keys: [
    //     "industry_change__pos_pred_n",
    //     "industry_change__no_change_pos_pred_n",
    //     "industry_change__no_change_neg_pred_n",
    //     "industry_change__neg_pred_n",
    //   ],
    //   labels: {
    //     "industry_change__pos_pred_n":          "Changed to more positive",
    //     "industry_change__no_change_pos_pred_n":"Stayed positive",
    //     "industry_change__no_change_neg_pred_n":"Stayed negative",
    //     "industry_change__neg_pred_n":          "Changed to more negative",
    //   },
    //   colors: {
    //     "industry_change__pos_pred_n":          "#1A7A78",
    //     "industry_change__no_change_pos_pred_n":"#A8D5D4",
    //     "industry_change__no_change_neg_pred_n":"#F4A69A",
    //     "industry_change__neg_pred_n":          "#C0392B",
    //   },
    //   schema: "single",
    // },

  };

  return { groupsById };
}

export function buildThemeLabelColorMaps(groupsById) {
  const labels = {};
  const colors = {};

  for (const g of Object.values(groupsById)) {
    for (const k of g.keys) {
      labels[k] = g.labels?.[k] || k;
      if (!colors[k]) {
        colors[k] = g.colors?.[k] ?? baseColorForLikertKey(g, k);
      }
    }
  }

  return { labels, colors };
}

export function baseColorForLikertKey(group, key) {
  const pol = group?.polarity || {};
  const tag = Object.entries(pol).find(([_, polKey]) => {
    if (Array.isArray(polKey)) return polKey.includes(key);
    return polKey === key;
  })?.[0];
  if (tag === "pos") return "#1A7A78";
  if (tag === "neg") return "#C0392B";
  if (tag === "neu") return "#8C9BA5";
  return "#607D8B";
}

// Tab-as-config default export
export default {
  tab: "invariant_message",
  label: "Invariant Messaging",
  get metrics() { return getThemeConfig().groupsById; },
};
